// AI Employees data for frontend use
// This is a simplified version of the worker data

export interface AIEmployee {
  id: string;
  name: string;
  role: string;
  description: string;
  avatar: string; // Emoji avatar
  color: string;
  gradient: string;
  category: string;
  capabilities: string[];
  isNew?: boolean;
  isComingSoon?: boolean;
}

export const AI_EMPLOYEES: AIEmployee[] = [
  {
    id: 'agent',
    name: 'Agent',
    role: 'Conversational Agent',
    description: 'Your universal AI assistant and expert on all subjects. Ask anything and get intelligent answers, or get connected to the right specialist for your needs.',
    avatar: '🤖',
    color: '#0ea5e9',
    gradient: 'from-sky-500 to-blue-500',
    category: 'general',
    capabilities: [
      'General knowledge on any topic',
      'Intelligent question answering',
      'Smart routing to specialists',
      'Research and information gathering',
      'Problem solving and advice',
      'Multi-domain expertise',
    ],
  },
  {
    id: 'aria',
    name: 'Aria',
    role: 'Social Media Manager',
    description: 'Expert at creating viral content, managing social presence, and building engaged communities across all platforms.',
    avatar: '🎀',
    color: '#ec4899',
    gradient: 'from-pink-500 to-rose-500',
    category: 'marketing',
    capabilities: [
      'Content calendar planning',
      'Post creation and scheduling',
      'Hashtag research and optimization',
      'Engagement analytics',
      'Trend monitoring',
      'Community management',
    ],
  },
  {
    id: 'nova',
    name: 'Nova',
    role: 'Customer Support',
    description: 'Delivers exceptional customer experiences with empathy, efficiency, and expertise in resolving any inquiry.',
    avatar: '💙',
    color: '#06b6d4',
    gradient: 'from-cyan-500 to-teal-500',
    category: 'support',
    capabilities: [
      'Multi-channel support',
      'Ticket management',
      'FAQ creation',
      'Response templates',
      'Escalation handling',
      'Customer sentiment analysis',
    ],
  },
  {
    id: 'atlas',
    name: 'Atlas',
    role: 'Data Analyst & Algorithm Expert',
    description: 'Expert in algorithms, data analysis, and computational problem-solving. Writes textbook-quality algorithms, analyzes data files, reads algorithm questions from images, and makes data-driven decisions.',
    avatar: '📊',
    color: '#8b5cf6',
    gradient: 'from-violet-500 to-purple-500',
    category: 'operations',
    capabilities: [
      'Algorithm design and writing',
      'Data analysis and interpretation',
      'Image analysis for algorithms',
      'Data file processing (CSV, JSON)',
      'Statistical analysis',
      'Decision making from data',
    ],
  },
  {
    id: 'sage',
    name: 'Sage',
    role: 'Business Strategist',
    description: 'Provides expert strategic guidance to drive growth, optimize operations, and outmaneuver competition.',
    avatar: '🧠',
    color: '#10b981',
    gradient: 'from-emerald-500 to-green-500',
    category: 'operations',
    capabilities: [
      'Business strategy development',
      'Market analysis',
      'Growth planning',
      'Competitive intelligence',
      'SWOT analysis',
      'Business model optimization',
    ],
  },
  {
    id: 'echo',
    name: 'Echo',
    role: 'Email Marketing',
    description: 'Crafts high-converting email campaigns that nurture leads and drive consistent revenue growth.',
    avatar: '📧',
    color: '#f97316',
    gradient: 'from-orange-500 to-amber-500',
    category: 'marketing',
    capabilities: [
      'Email campaign creation',
      'Newsletter design',
      'Automation sequences',
      'A/B testing strategies',
      'Subject line optimization',
      'List segmentation',
    ],
  },
  {
    id: 'pulse',
    name: 'Pulse',
    role: 'Personal Growth',
    description: 'Your dedicated coach for wellness, productivity, and personal development goals.',
    avatar: '💪',
    color: '#ef4444',
    gradient: 'from-red-500 to-rose-500',
    category: 'personal',
    capabilities: [
      'Goal setting and tracking',
      'Productivity systems',
      'Habit building',
      'Time management',
      'Wellness planning',
      'Workout routines',
    ],
  },
  {
    id: 'scout',
    name: 'Scout',
    role: 'Recruiter',
    description: 'Expert at finding, screening, and onboarding top talent that fits your culture.',
    avatar: '🔍',
    color: '#14b8a6',
    gradient: 'from-teal-500 to-cyan-500',
    category: 'operations',
    capabilities: [
      'Job description writing',
      'Candidate sourcing',
      'Resume screening',
      'Interview questions',
      'Onboarding plans',
      'Employer branding',
    ],
  },
  {
    id: 'ink',
    name: 'Ink',
    role: 'Copywriter',
    description: 'Creates compelling copy that converts readers into customers with powerful words.',
    avatar: '✍️',
    color: '#6366f1',
    gradient: 'from-indigo-500 to-violet-500',
    category: 'creative',
    capabilities: [
      'Ad copy creation',
      'Landing page copy',
      'Blog writing',
      'Product descriptions',
      'Brand messaging',
      'Headlines and taglines',
    ],
  },
  {
    id: 'flux',
    name: 'Flux',
    role: 'eCommerce Manager',
    description: 'Optimizes your online store for maximum conversions and customer satisfaction.',
    avatar: '🛒',
    color: '#f59e0b',
    gradient: 'from-amber-500 to-yellow-500',
    category: 'sales',
    capabilities: [
      'Product listing optimization',
      'Pricing strategy',
      'Inventory management',
      'Conversion optimization',
      'Customer journey mapping',
      'Upsell strategies',
    ],
  },
  {
    id: 'spark',
    name: 'Spark',
    role: 'Sales Specialist',
    description: 'Closes deals with persuasive outreach, compelling pitches, and strategic follow-ups.',
    avatar: '🎯',
    color: '#22c55e',
    gradient: 'from-green-500 to-emerald-500',
    category: 'sales',
    capabilities: [
      'Cold email sequences',
      'Sales pitch creation',
      'Objection handling',
      'Follow-up strategies',
      'Proposal writing',
      'CRM optimization',
    ],
  },
  {
    id: 'orbit',
    name: 'Orbit',
    role: 'SEO Expert',
    description: 'Dominates search rankings with strategic optimization and content that drives organic traffic.',
    avatar: '🚀',
    color: '#3b82f6',
    gradient: 'from-blue-500 to-indigo-500',
    category: 'marketing',
    capabilities: [
      'Keyword research',
      'On-page optimization',
      'Content strategy',
      'Technical SEO audits',
      'Backlink strategies',
      'Local SEO',
    ],
  },
  {
    id: 'sync',
    name: 'Sync',
    role: 'Virtual Assistant',
    description: 'Your reliable assistant for scheduling, organization, and daily task management.',
    avatar: '📅',
    color: '#a855f7',
    gradient: 'from-purple-500 to-fuchsia-500',
    category: 'personal',
    capabilities: [
      'Calendar management',
      'Meeting scheduling',
      'Task prioritization',
      'Travel planning',
      'Expense tracking',
      'Research tasks',
    ],
  },
  {
    id: 'code',
    name: 'Code',
    role: 'Developer Assistant',
    description: 'Your coding companion for debugging, documentation, and development best practices.',
    avatar: '💻',
    color: '#64748b',
    gradient: 'from-slate-500 to-gray-500',
    category: 'technical',
    capabilities: [
      'Code review',
      'Bug debugging',
      'Documentation writing',
      'API design',
      'Performance optimization',
      'Security audits',
    ],
  },
  {
    id: 'pixel',
    name: 'Pixel',
    role: 'Design Assistant',
    description: 'Provides expert design feedback and maintains your visual brand consistency.',
    avatar: '🎨',
    color: '#f472b6',
    gradient: 'from-pink-400 to-rose-400',
    category: 'creative',
    capabilities: [
      'Design feedback',
      'Brand guidelines',
      'UI/UX suggestions',
      'Color palette creation',
      'Typography selection',
      'Visual hierarchy',
    ],
  },
  {
    id: 'legal',
    name: 'Legal',
    role: 'Compliance Assistant',
    description: 'Helps navigate legal requirements with policy drafting and compliance guidance.',
    avatar: '⚖️',
    color: '#78716c',
    gradient: 'from-stone-500 to-neutral-500',
    category: 'operations',
    capabilities: [
      'Policy drafting',
      'Terms of service',
      'Privacy policy',
      'Contract review',
      'GDPR compliance',
      'Risk assessment',
    ],
  },
  {
    id: 'mvp',
    name: 'MVP Maker',
    role: 'Prototype Builder',
    description: 'Transforms your ideas into fully functional prototypes. From concept to production-ready MVP in one conversation.',
    avatar: '🚀',
    color: '#8b5cf6',
    gradient: 'from-violet-500 to-indigo-500',
    category: 'technical',
    isComingSoon: true,
    capabilities: [
      'Full-stack prototyping',
      'Architecture design',
      'Database schema',
      'API development',
      'Frontend UI/UX',
      'Deployment setup',
    ],
  },
  {
    id: 'slides',
    name: 'Slides',
    role: 'Presentation Maker',
    description: 'Creates stunning professional presentations with animations, transitions, and beautiful typography. Like Gamma AI, Tome, and Canva.',
    avatar: '📊',
    color: '#f97316',
    gradient: 'from-orange-500 to-red-500',
    category: 'creative',
    capabilities: [
      'Professional slide design',
      'Animated presentations',
      'Beautiful typography',
      'Slide transitions & effects',
      'Content structure & flow',
      'Visual storytelling',
      'Data visualization slides',
      'Pitch deck creation',
    ],
  },
];

// Helper functions
export function getEmployeeById(id: string) {
  return AI_EMPLOYEES.find((e) => e.id === id);
}

export function getEmployeesByCategory(category: string) {
  return AI_EMPLOYEES.filter((e) => e.category === category);
}

export const EMPLOYEE_CATEGORIES = [
  { id: 'general', name: 'General', icon: 'bot' },
  { id: 'marketing', name: 'Marketing', icon: 'megaphone' },
  { id: 'sales', name: 'Sales', icon: 'trending-up' },
  { id: 'support', name: 'Support', icon: 'headphones' },
  { id: 'operations', name: 'Operations', icon: 'settings' },
  { id: 'creative', name: 'Creative', icon: 'palette' },
  { id: 'technical', name: 'Technical', icon: 'code' },
  { id: 'personal', name: 'Personal', icon: 'user' },
];
